import ApiService from './apiService';

/**
 * Service pour gérer les retraits de stock (stock withdrawals)
 */
class StockWithdrawalService {
    /**
     * Récupérer la liste des retraits de stock avec filtres et pagination
     * @param {Object} params Paramètres de filtrage et pagination
     *   - status: Filtrer par statut
     *   - reason: Filtrer par raison
     *   - start_date: Date de début
     *   - end_date: Date de fin
     *   - search: Recherche par référence
     *   - per_page: Nombre d'éléments par page
     *   - page: Numéro de page
     * @returns {Promise} Réponse de l'API avec la liste des retraits
     */
    getStockWithdrawals(params = {}) {
        return ApiService.get('/stock-withdrawals', params);
    }

    /**
     * Récupérer un retrait de stock par son ID
     * @param {Number} id ID du retrait
     * @returns {Promise} Réponse de l'API avec les détails du retrait
     */
    getStockWithdrawal(id) {
        return ApiService.get(`/stock-withdrawals/${id}`);
    }

    /**
     * Créer un nouveau retrait de stock
     * @param {Object} withdrawalData Données du retrait
     *   - withdrawal_date: Date du retrait (requis)
     *   - reason: Raison du retrait (requis) - doit être dans REASON_LIST
     *   - notes: Notes optionnelles
     *   - reference: Référence optionnelle (générée automatiquement si non fournie)
     *   - items: Tableau d'éléments (requis)
     *     - product_id: ID du produit (requis)
     *     - quantity: Quantité à retirer (requis, min: 0.01)
     *     - notes: Notes optionnelles pour l'élément
     * @returns {Promise} Réponse de l'API avec le retrait créé
     */
    createStockWithdrawal(withdrawalData) {
        return ApiService.post('/stock-withdrawals', withdrawalData);
    }

    /**
     * Mettre à jour un retrait de stock existant
     * @param {Number} id ID du retrait à mettre à jour
     * @param {Object} withdrawalData Nouvelles données du retrait
     *   - status: Statut du retrait (optionnel)
     *   - notes: Notes (optionnel)
     * @returns {Promise} Réponse de l'API avec le retrait mis à jour
     */
    updateStockWithdrawal(id, withdrawalData) {
        return ApiService.put(`/stock-withdrawals/${id}`, withdrawalData);
    }

    /**
     * Supprimer un retrait de stock
     * @param {Number} id ID du retrait à supprimer
     * @returns {Promise} Réponse de l'API
     */
    deleteStockWithdrawal(id) {
        return ApiService.delete(`/stock-withdrawals/${id}`);
    }

    /**
     * Télécharger le PDF d'un retrait de stock
     * @param {Number} id ID du retrait de stock
     * @returns {Promise} Blob PDF à télécharger
     */
    downloadStockWithdrawalPdf(id) {
        return ApiService.download(`/stock-withdrawals/${id}/download`, {}, `retrait-stock-${id}.pdf`);
    }

    /**
     * Exporter un retrait de stock en Excel ou CSV
     * @param {Number} id ID du retrait de stock
     * @param {Object} [options] { format: 'csv' | 'excel' }
     * @returns {Promise} Blob fichier exporté à télécharger
     */
    exportStockWithdrawal(id, options = {}) {
        const params = {};
        if (options.format) {
            params.format = options.format;
        }
        // Le backend définit le nom du fichier dans Content-Disposition header
        // On passe une chaîne vide pour forcer l'extraction depuis les headers
        return ApiService.download(`/stock-withdrawals/${id}/export`, params, '');
    }

    /**
     * Obtenir le libellé de la raison en français
     * @param {String} reason Code de la raison
     * @returns {String} Libellé traduit
     */
    getReasonLabel(reason) {
        const labels = {
            'damage': 'Dommage/Casse',
            'loss': 'Perte',
            'theft': 'Vol',
            'expired': 'Expiré',
            'return_to_supplier': 'Retour fournisseur',
            'adjustment': 'Ajustement',
            'other': 'Autre'
        };
        return labels[reason] || reason;
    }

    /**
     * Obtenir la couleur associée à la raison
     * @param {String} reason Code de la raison
     * @returns {String} Classe CSS ou code couleur
     */
    getReasonColor(reason) {
        const colors = {
            'damage': 'warning',      // Orange/Jaune
            'loss': 'danger',         // Rouge
            'theft': 'danger',        // Rouge
            'expired': 'warning',     // Orange
            'return_to_supplier': 'info', // Bleu clair
            'adjustment': 'secondary', // Gris
            'other': 'secondary'      // Gris
        };
        return colors[reason] || 'secondary';
    }

    /**
     * Obtenir le libellé du statut en français
     * @param {String} status Code du statut
     * @returns {String} Libellé traduit
     */
    getStatusLabel(status) {
        const labels = {
            'draft': 'Brouillon',
            'completed': 'Complété',
            'cancelled': 'Annulé'
        };
        return labels[status] || status;
    }

    /**
     * Obtenir la couleur associée au statut
     * @param {String} status Code du statut
     * @returns {String} Classe CSS ou code couleur
     */
    getStatusColor(status) {
        const colors = {
            'draft': 'secondary',     // Gris
            'completed': 'success',   // Vert
            'cancelled': 'danger'     // Rouge
        };
        return colors[status] || 'secondary';
    }

    /**
     * Formater un retrait de stock pour l'affichage
     * @param {Object} withdrawal Données du retrait
     * @returns {Object} Retrait formaté avec informations additionnelles
     */
    formatWithdrawal(withdrawal) {
        if (!withdrawal) return null;

        return {
            ...withdrawal,
            reason_label: this.getReasonLabel(withdrawal.reason),
            reason_color: this.getReasonColor(withdrawal.reason),
            status_label: this.getStatusLabel(withdrawal.status),
            status_color: this.getStatusColor(withdrawal.status),
            formatted_date: this.formatDate(withdrawal.withdrawal_date),
            total_items: withdrawal.items ? withdrawal.items.length : 0,
            total_quantity: this.calculateTotalQuantity(withdrawal.items)
        };
    }

    /**
     * Formater une date pour l'affichage
     * @param {String|Date} date Date à formater
     * @returns {String} Date formatée
     */
    formatDate(date) {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    /**
     * Formater une date avec l'heure pour l'affichage
     * @param {String|Date} date Date à formater
     * @returns {String} Date et heure formatées
     */
    formatDateTime(date) {
        if (!date) return '-';
        return new Date(date).toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Formater un montant en devise
     * @param {Number} amount Montant à formater
     * @returns {String} Montant formaté
     */
    formatCurrency(amount) {
        if (amount === undefined || amount === null) return '-';
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0
        }).format(amount);
    }

    /**
     * Calculer la quantité totale des éléments d'un retrait
     * @param {Array} items Liste des éléments
     * @returns {Number} Quantité totale
     */
    calculateTotalQuantity(items) {
        if (!items || !items.length) return 0;
        return items.reduce((total, item) => {
            return total + (parseFloat(item.quantity) || 0);
        }, 0);
    }

    /**
     * Calculer le montant total des éléments d'un retrait
     * @param {Array} items Liste des éléments
     * @returns {Number} Montant total
     */
    calculateTotalAmount(items) {
        if (!items || !items.length) return 0;
        return items.reduce((total, item) => {
            const quantity = parseFloat(item.quantity) || 0;
            const unitPrice = parseFloat(item.unit_price) || 0;
            return total + (quantity * unitPrice);
        }, 0);
    }

    /**
     * Filtrer les retraits par période
     * @param {Array} withdrawals Liste des retraits
     * @param {String} startDate Date de début (format ISO)
     * @param {String} endDate Date de fin (format ISO)
     * @returns {Array} Retraits filtrés
     */
    filterByDateRange(withdrawals, startDate, endDate) {
        if (!withdrawals || !withdrawals.length) return [];
        if (!startDate || !endDate) return withdrawals;

        const start = new Date(startDate);
        const end = new Date(endDate);

        return withdrawals.filter(withdrawal => {
            const withdrawalDate = new Date(withdrawal.withdrawal_date);
            return withdrawalDate >= start && withdrawalDate <= end;
        });
    }

    /**
     * Grouper les retraits par raison
     * @param {Array} withdrawals Liste des retraits
     * @returns {Object} Retraits groupés par raison
     */
    groupByReason(withdrawals) {
        if (!withdrawals || !withdrawals.length) return {};

        return withdrawals.reduce((groups, withdrawal) => {
            const reason = withdrawal.reason;
            if (!groups[reason]) {
                groups[reason] = [];
            }
            groups[reason].push(withdrawal);
            return groups;
        }, {});
    }

    /**
     * Grouper les retraits par statut
     * @param {Array} withdrawals Liste des retraits
     * @returns {Object} Retraits groupés par statut
     */
    groupByStatus(withdrawals) {
        if (!withdrawals || !withdrawals.length) return {};

        return withdrawals.reduce((groups, withdrawal) => {
            const status = withdrawal.status;
            if (!groups[status]) {
                groups[status] = [];
            }
            groups[status].push(withdrawal);
            return groups;
        }, {});
    }

    /**
     * Calculer les statistiques des retraits
     * @param {Array} withdrawals Liste des retraits
     * @returns {Object} Statistiques (total, par raison, par statut)
     */
    calculateStats(withdrawals) {
        if (!withdrawals || !withdrawals.length) {
            return {
                total: 0,
                totalQuantity: 0,
                totalAmount: 0,
                byReason: {},
                byStatus: {}
            };
        }

        const stats = {
            total: withdrawals.length,
            totalQuantity: 0,
            totalAmount: 0,
            byReason: {},
            byStatus: {}
        };

        withdrawals.forEach(withdrawal => {
            // Total quantité
            const quantity = this.calculateTotalQuantity(withdrawal.items);
            stats.totalQuantity += quantity;

            // Total montant
            const amount = this.calculateTotalAmount(withdrawal.items);
            stats.totalAmount += amount;

            // Par raison
            const reason = withdrawal.reason;
            if (!stats.byReason[reason]) {
                stats.byReason[reason] = {
                    count: 0,
                    quantity: 0,
                    amount: 0
                };
            }
            stats.byReason[reason].count++;
            stats.byReason[reason].quantity += quantity;
            stats.byReason[reason].amount += amount;

            // Par statut
            const status = withdrawal.status;
            if (!stats.byStatus[status]) {
                stats.byStatus[status] = {
                    count: 0,
                    quantity: 0,
                    amount: 0
                };
            }
            stats.byStatus[status].count++;
            stats.byStatus[status].quantity += quantity;
            stats.byStatus[status].amount += amount;
        });

        return stats;
    }

    /**
     * Valider les données d'un retrait avant envoi
     * @param {Object} withdrawalData Données du retrait à valider
     * @returns {Object} { valid: boolean, errors: Array }
     */
    validateWithdrawalData(withdrawalData) {
        const errors = [];

        // Valider la date
        if (!withdrawalData.withdrawal_date) {
            errors.push('La date de retrait est requise');
        }

        // Valider la raison
        if (!withdrawalData.reason) {
            errors.push('La raison du retrait est requise');
        }

        // Valider les éléments
        if (!withdrawalData.items || !Array.isArray(withdrawalData.items) || withdrawalData.items.length === 0) {
            errors.push('Au moins un élément est requis');
        } else {
            withdrawalData.items.forEach((item, index) => {
                if (!item.product_id) {
                    errors.push(`L'élément ${index + 1} : le produit est requis`);
                }
                if (!item.quantity || parseFloat(item.quantity) <= 0) {
                    errors.push(`L'élément ${index + 1} : la quantité doit être supérieure à 0`);
                }
            });
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }
}

export default new StockWithdrawalService();

